



















import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import BaseDoughnut from '@/components/Charts/BaseDoughnut';
import { DemographicTenant } from '@/ts/HococoInterfaces';

@Component({
    components: {
        BaseDoughnut,
    },
})
export default class DemographicChart extends Vue {
    @Prop() public demographicTenants: DemographicTenant[];
    @Prop() public mode: string;

    public chartData: any = null;
    public options: any = null;

    protected legend: string = '';

    protected currentHue: number = 220;
    protected currentSaturation: number = 79;
    protected currentValue: number = 61;
    protected colourIncrementValue: number = 35;

    constructor() {
        super();
    }

    get isDonutChart(): boolean {
        return (this.mode === 'age' || this.mode === 'gender' || this.mode === 'educations' || this.mode === 'socialClubs');
    }

    get chartReady(): boolean {
        return (this.chartData && this.chartData.labels.length >= 1);
    }

    @Watch('demographicTenants')
    public demographicTenantsChanged() {
        if (this.demographicTenants && this.demographicTenants.length > 0) {
            this.renderChart();
        }
    }    

    get chartDataProp(): object {
        if (this.chartData) {
            return this.chartData;
        }

        return {};
    }

    get optionsProp(): object {
        if (this.options) {
            return this.options.options;
        }
        
        return {
            legend: {
                display: false,
            },
        };
    }

    public getLegendIndex(id: number) {
        return 'legend-' + id.toString() + '-item';
    }

    public getLegendLabel(id: number) {
        return this.chartData.labels[id] + ': ' + (this.chartData.datasets[0].data[id] * 100).toFixed(2) + '%';
    }

    public getLegendStyles(id: number) {
        return {
            'background-color': this.chartData.datasets[0].backgroundColor[id],
        };
    }
    
    public getChartMeta() {
        const allMeta = this.chartData.datasets[0]._meta;
        const metaKeys = Object.keys(allMeta);
        return this.chartData.datasets[0]._meta[metaKeys[0]];
    }

    public legendItemClickHandler(index: number) {
        const legendItemSelector = '.legend-item';
        const labelSeletor = '.label';
        const meta = this.getChartMeta();
        const result = meta.data[index].hidden === true ? false : true;
        
        if (result === true) {
            meta.data[index].hidden = true;
            // labelEl.style.textDecoration = "line-through";
        } else {
            // labelEl.style.textDecoration = "none";
            meta.data[index].hidden = false;
        }

        this.updateChart();
    }

    public populateAgeData() {
        let total = 0;
        
        const counts = {
            '?': 0,
        } as {[key: string]: any};

        const percentages = {} as {[key: string]: any};

        const actualData = this.demographicTenants.map((element) => {
            return element.age;
        });

        for (const age of actualData) {
            total++;

            if (age === null) {
                counts['?']++;
                continue;
            }

            if (!counts.hasOwnProperty(age.toString())) {
                counts[age.toString()] = 0;
            }

            counts[age.toString()]++;
        }

        for (const age of Object.entries(counts)) {
            percentages[age[0]] = age[1] / total;
        }

        this.chartData = this.generateChartData(Object.values(percentages), Object.keys(percentages));
        this.options = this.generateChartConfig(Object.values(percentages), Object.keys(percentages));        
    }

    public generateChartConfig(dataItems: any, labels: any) {
        const config = {
            responsive: true,
            type: 'doughnut',
            legend: {
                display: false,
            },                
            title: {
                display: false,
            },
            tooltips: {
                displayColors: false,
                callbacks: {
                    label: (tooltipItem: any, data: any) => {
                        const label = data.labels[tooltipItem.index];
                        const val = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                        return label + ' : ' + val.toFixed(2);
                    },
                },
            },            
        };

        return config;
    }

    public generateChartData(dataItems: any, labels: any) {
        return {
            labels,
            datasets: [{
                data: dataItems,
                backgroundColor: this.generateBackgroundColors(dataItems.length),
                borderWidth: 1,
            }],
        };
    }

    public generateBackgroundColors(count: number) {
        const output = [];
        this.colourIncrementValue = (365 / count);
        
        for (let iterator = 0; iterator < count; iterator++) {
            output.push(this.getChartColor());
        }

        return output;
    }

    public populateGenderData() {
        let total = 0;
        
        const counts = {
            '?': 0,
        } as {[key: string]: any};

        const percentages = {} as {[key: string]: any};

        const actualData = this.demographicTenants.map((element) => {
            return element.gender;
        });

        for (const gender of actualData) {
            total++;

            if (gender === null) {
                counts['?']++;
                continue;
            }

            if (!counts.hasOwnProperty(gender.toString())) {
                counts[gender.toString()] = 0;
            }

            counts[gender.toString()]++;
        }

        for (const age of Object.entries(counts)) {
            percentages[age[0]] = age[1] / total;
        }

        this.chartData = this.generateChartData(Object.values(percentages), Object.keys(percentages));
        this.options = this.generateChartConfig(Object.values(percentages), Object.keys(percentages));        
    }

    public populateEducationsData() {
        let total = 0;
        
        const counts = {} as {[key: string]: any};

        const percentages = {} as {[key: string]: any};

        const actualData = this.demographicTenants.map((element) => {
            return element.educations;
        });

        for (const educations of actualData) {
            for (const education of educations) {
                total++;                

                if (!counts.hasOwnProperty(education.toString())) {
                    counts[education.toString()] = 0;
                }

                counts[education.toString()]++;
            }
        }

        for (const education of Object.entries(counts)) {
            percentages[education[0]] = education[1] / total;
        }

        this.chartData = this.generateChartData(Object.values(percentages), Object.keys(percentages));
        this.options = this.generateChartConfig(Object.values(percentages), Object.keys(percentages));        
    }

    public populateSocialClubsData() {
        let total = 0;
        
        const counts = {} as {[key: string]: any};

        const percentages = {} as {[key: string]: any};

        const actualData = this.demographicTenants.map((element) => {
            return element.socialClubs;
        });

        for (const socialClubs of actualData) {
            for (const socialClub of socialClubs) {
                total++;                

                if (!counts.hasOwnProperty(socialClub.toString())) {
                    counts[socialClub.toString()] = 0;
                }

                counts[socialClub.toString()]++;
            }
        }

        for (const socialClub of Object.entries(counts)) {
            percentages[socialClub[0]] = socialClub[1] / total;
        }

        this.chartData = this.generateChartData(Object.values(percentages), Object.keys(percentages));
        this.options = this.generateChartConfig(Object.values(percentages), Object.keys(percentages));
    }     

    public renderChart() {
        switch (this.mode) {
            case 'age':
                this.populateAgeData();
                break;

            case 'gender':
                this.populateGenderData();
                break;

            case 'educations':
                this.populateEducationsData();
                break;

            case 'socialClubs':
                this.populateSocialClubsData();
                break;
        }

        Vue.nextTick(() => {
            (this.$refs.chart as any).renderChart(this.chartData, this.options);
        });
    }

    public updateChart() {
        (this.$refs.chart as any).$data._chart.update();
    }

    public getChartColor() {
        const color = this.hslToHex(this.currentHue, this.currentSaturation, this.currentValue);
        
        let targetHue = this.currentHue + this.colourIncrementValue;

        if (targetHue > 365) {
            targetHue -= 365;
        }

        this.currentHue = targetHue;

        return color;
    }

    public hslToHex(h: number, s: number, l: number) {
        s /= 100;
        l /= 100;

        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((h / 60) % 2 - 1));
        const m = l - c / 2;
        let r = 0;
        let g = 0;
        let b = 0;

        if (0 <= h && h < 60) {
            r = c;
            g = x;
            b = 0;
        } else if (60 <= h && h < 120) {
            r = x;
            g = c;
            b = 0;
        } else if (120 <= h && h < 180) {
            r = 0;
            g = c;
            b = x;
        } else if (180 <= h && h < 240) {
            r = 0;
            g = x;
            b = c;
        } else if (240 <= h && h < 300) {
            r = x;
            g = 0;
            b = c;
        } else if (300 <= h && h < 360) {
            r = c;
            g = 0;
            b = x;
        }

        // Having obtained RGB, convert channels to hex
        let rString = Math.round((r + m) * 255).toString(16);
        let gString = Math.round((g + m) * 255).toString(16);
        let bString = Math.round((b + m) * 255).toString(16);

        // Prepend 0s, if necessary
        if (rString.length === 1) {
            rString = '0' + r;
        }

        if (gString.length === 1) {
            gString = '0' + g;
        }

        if (bString.length === 1) {
            bString = '0' + b;
        }

        return '#' + rString + gString + bString;
    }
}
